'use client';

import { useMemo, useRef, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { FileImage } from 'lucide-react';
import { PromoBenefits } from '@/shared/ui';
import { useJobQueueStore } from '@/features/design/job-queue';
import { useAlbum } from '../hooks/use-album';
import { useAlbumIntro } from '../hooks/use-album-intro';
import { AlbumsList } from './AlbumsList';
import { mergeAlbumsWithJobs } from '../model/merge-albums-with-jobs';

interface AlbumListWithViewerProps {
  conceptId: string;
  projectId: string;
  conceptTitle?: string | null;
  conceptNumber?: number;
  coverImageUrl?: string | null;
}

/**
 * Two-column right pane: just the list of albums with cover thumbnails.
 *
 * Apple parity: shares `AlbumListRow` (via `AlbumsList`) with the single-column
 * `AlbumCollapsiblePanel`, so the cover, title, format/language line, date,
 * and action icons are identical. The previous incarnation rendered the
 * cover-less `AlbumItem` plus an inline iframe PDF preview; Apple has no such
 * preview (it presents the PDF fullscreen via `.albumPDFViewer`), so the
 * iframe was removed for cross-platform parity. Clicking an album's link
 * action still opens the public URL — the row's tap targets are the action
 * icons themselves.
 */
export function AlbumListWithViewer({
  conceptId,
  conceptTitle,
  conceptNumber,
  coverImageUrl,
}: AlbumListWithViewerProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const { albums, deleteAlbum, isDeleting, downloadAlbum } = useAlbum({
    conceptId,
  });

  // Server-driven benefits copy for the empty state (Apple/Android intro
  // parity) — advertises what a project album includes now that albums are a
  // pack-gated feature, instead of a bare "no blueprints" placeholder.
  const { data: intro } = useAlbumIntro(conceptId);

  // Subscribe to in-flight album jobs so generating / pending rows appear
  // immediately, matching `AlbumCollapsiblePanel`'s merge behaviour.
  const albumJobsFromQueue = useJobQueueStore(
    useShallow((state) => state.getAllAlbumsForConcept?.(conceptId) || [])
  );

  // Same merge as `useAlbumPanel.mergedAlbums` (mergeAlbumsWithJobs).
  const mergedAlbums = useMemo(
    () => mergeAlbumsWithJobs(albums, albumJobsFromQueue),
    [albumJobsFromQueue, albums]
  );


  // Scroll the list to top whenever a fresh album lands at the front —
  // mirrors `AlbumGeneratorView.twoColumnContent`'s `ScrollViewReader`.
  const firstAlbumId = mergedAlbums[0]?.id;
  useEffect(() => {
    if (scrollRef.current && firstAlbumId) {
      scrollRef.current.scrollTop = 0;
    }
  }, [firstAlbumId]);

  if (mergedAlbums.length === 0) {
    // Wait for the intro copy before painting the promo so it never flashes a
    // half-empty card. The settings inspector (right column) is always shown
    // regardless — this is the content column.
    if (!intro) return null;
    return (
      <div className="h-full overflow-y-auto px-4 pb-6">
        <div className="mx-auto w-full max-w-[560px] pt-2">
          <PromoBenefits
            icon={<FileImage />}
            title={intro.title}
            subtitle={intro.subtitle}
            bullets={intro.bullets}
          />
        </div>
      </div>
    );
  }

  // `px-4 pb-6` gives the album cover's box-shadow room before the scroll
  // container's `overflow-y-auto` (which forces `overflow-x` to clip on most
  // engines too) chops the soft diffuse layer at the right/bottom edge —
  // same trap that `ProjectAlbumsSection`'s `pb-7` was added to fix on the
  // dashboard carousel.
  return (
    <div ref={scrollRef} className="h-full overflow-y-auto px-4 pb-6">
      <AlbumsList
        albums={mergedAlbums}
        highlightedAlbumId={null}
        isDeleting={isDeleting}
        onDelete={deleteAlbum}
        onDownload={downloadAlbum}
        conceptTitle={conceptTitle}
        conceptNumber={conceptNumber}
        coverImageUrl={coverImageUrl}
        twoUp
      />
    </div>
  );
}
